import { useEffect } from "react";
import { z } from "zod";

import { useDebouncedValue } from "lib/debounce";
import { useURLState } from "lib/hooks/useURLState";
import { LoggedAction, LoggedEntityType } from "lib/types/graphQLEnums";
import { useLogActivity } from "./useLogActivity";

// Long enough that typing a keyword or ticking several filters counts as one search
const SEARCH_SETTLE_MS = 1500;

/**
 * Records a search when the page opens and each time its filters settle on a new value. Pass
 * `undefined` for filters while there is nothing to search yet.
 */
export function useLogSearch(entityType: LoggedEntityType, filters: unknown) {
  const { mutate: logActivity } = useLogActivity();
  const [settledFilters] = useDebouncedValue(
    filters === undefined ? undefined : JSON.stringify(filters),
    SEARCH_SETTLE_MS,
  );

  useEffect(() => {
    if (settledFilters !== undefined) {
      logActivity({ input: { action: LoggedAction.Search, entityType } });
    }
    // logActivity is a new function each render, so it would log a search every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, settledFilters]);
}

/** `useLogSearch` keyed on a search page's filters URL param, for pages that don't hold them. */
export function useLogSearchOfURLFilters(entityType: LoggedEntityType, urlKey: string) {
  const [rawFilters] = useURLState(urlKey, null, z.unknown());
  useLogSearch(entityType, rawFilters);
}
